/** @jsxImportSource @emotion/react */
import { useContext, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { type KolSearchForm, type KolSearchRecommendation, type KolSearchStatus, KolSearchStatusEnum } from '@trinity/chat';
import { CancelAction, DownloadQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint/index.js';
import {
  KolSearchFormContext,
  KolSearchResultsContext,
  KolSearchCancelContext,
  KolSearchStatusContext,
  UpdateKolSearchProfileContext,
  KolResetSearchContext,
  KolSearchIdContext,
  KolSearchResultsFilterContext,
  KolSearchResultsSortContext,
} from './context';
import { GenerateRouteButton } from './GenerateRouteButton';
import { useDownloadKolSearch } from './hooks/useDownloadKolSearch';
import { useIsSmartRouting } from './hooks/useIsSmartRouting';
import { KolResultsContainer, KolResultsTable  } from './KolSearch.Table';
import { KolTableContainer } from './KolTableContainer';
import { BasicSystemUserMessage } from './Message.System.Basic';

export const KolResultsMessage = () => {
  const status = useContext(KolSearchStatusContext);
  const formState = useContext(KolSearchFormContext);
  const results = useContext(KolSearchResultsContext);
  const kolSearchId = useContext(KolSearchIdContext);
  const cancelSearch = useContext(KolSearchCancelContext);
  const selectProfile = useContext(UpdateKolSearchProfileContext);
  const handleNewSearch = useContext(KolResetSearchContext);
  const { download: downloadKolSearch } = useDownloadKolSearch({ kolSearchId });
  const isSmartRouting = useIsSmartRouting();

  const copy = `${buildMessageFilter(formState)}. Select a profile by clicking "View" and ask additional questions.`;

  // Keep the table visible if:
  // 1. Status is not ProfileSelected (normal case), OR
  // 2. Status is ProfileSelected but we have multiple results (indicating profile was selected from results table)
  const shouldShowTable = status !== KolSearchStatusEnum.ProfileSelected ||
    (status === KolSearchStatusEnum.ProfileSelected && results && results.length > 1);

  return (
    shouldShowTable && (
      <MessageContainer>
        <BasicSystemUserMessage bodyStyles={css({
          maxWidth: '100%',
        })}>
          <Body>
            <KolTableContainer pageSize={10}>
              <KolResultsContainer records={results}>
                {copy}
                <KolResultsTableWithInteractivity
                  onSelectProfile={selectProfile}
                  status={status}
                  downloadKolSearch={downloadKolSearch}
                  handleNewSearch={handleNewSearch}
                  cancelSearch={cancelSearch}
                  isSmartRouting={isSmartRouting}
                />
              </KolResultsContainer>
            </KolTableContainer>
          </Body>
        </BasicSystemUserMessage>
      </MessageContainer>
    )
  );
};

const KolResultsTableWithInteractivity = ({
  onSelectProfile,
  status,
  downloadKolSearch,
  handleNewSearch,
  cancelSearch,
  isSmartRouting,
}: {
  onSelectProfile: (kol: Pick<KolSearchRecommendation, 'id' | 'name'>) => void;
  status: KolSearchStatus;
  downloadKolSearch: () => void;
  handleNewSearch: () => void;
  cancelSearch: () => void;
  isSmartRouting: boolean;
}) => {
  // State to track if any footer action has been clicked
  const [isFooterActionClicked, setIsFooterActionClicked] = useState(false);

  // Now these contexts are available because we're inside KolTableContainer
  const filterContext = useContext(KolSearchResultsFilterContext);
  const sortState = useContext(KolSearchResultsSortContext);

  const filterState = filterContext?.state || [];
  const hasFiltersApplied = filterState.length > 0;
  const hasSortApplied = sortState ? Object.keys(sortState).length > 0 : false;
  const isActivelyFiltering = hasFiltersApplied || hasSortApplied;

  // Table should be interactive when there are multiple results OR when actively filtering/sorting
  // BUT NOT when a profile has been selected (to match recommendations table behavior)
  // AND NOT when any footer action has been clicked
  const isInteractive = (status === KolSearchStatusEnum.MultipleResultsReturned || isActivelyFiltering) &&
    status !== KolSearchStatusEnum.ProfileSelected &&
    !isFooterActionClicked;

  // Wrapper functions to disable table after action
  const handleNewSearchWithDisable = () => {
    setIsFooterActionClicked(true);
    handleNewSearch();
  };

  const handleCancelSearchWithDisable = () => {
    setIsFooterActionClicked(true);
    cancelSearch();
  };

  return (
    <>
      <StyledResultsTable
        onSelectProfile={onSelectProfile}
        isInteractive={isInteractive}
      />
      {isInteractive && (
        <>
          <StyledDownloadButton
            value="Download List"
            onClick={downloadKolSearch} />
          {isSmartRouting && (
            <div onClick={() => setIsFooterActionClicked(true)}>
              <GenerateRouteButton />
            </div>
          )}
          <StyledCancelButton
            displayValue="Start a new search"
            onClick={handleNewSearchWithDisable} />
          <StyledCancelButton
            displayValue="View recommended HCPs"
            onClick={handleCancelSearchWithDisable} />
        </>
      )}
    </>
  );
};

const StyledDownloadButton = styled(DownloadQueryHint)({
  marginTop: 15,
});

const StyledCancelButton = styled(CancelAction)({
  marginTop: 15,
});

const StyledResultsTable = styled(KolResultsTable)({
  marginTop: 15,
});

const Body = styled.div({
  // width: 725,
});

const MessageContainer = styled.div({
  paddingRight: 20,
  paddingLeft: 20,
});

function buildMessageFilter(formState: Partial<KolSearchForm>) {
  const filters: string[] = [];
  if (formState.name) filters.push(`matching the name "${formState.name}"`);

  if (formState.location) filters.push(`near ${formState.location.name ?? `the location (${formState.location.latitude}, ${formState.location.longitude})`}`);

  return `Sure, I found the following HCPs ${filters.join(' and ')}`;
}